import { useCallback, useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import type { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useClinicPortalServiceType } from '@/hooks/main/clinicPortalServiceType';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppModal from '@/hooks/useAppModal';
import type { TreatmentReportFormData } from '@/schemas/treatmentReportSchema';
import { searchTreatmentCount } from '@/services/main/treatmentReport';
import { japaneseDateToISO } from '@/utils/dateConverter';

interface UseTreatmentReportFrequencyParams {
  control: Control<TreatmentReportFormData>;
  watch: UseFormWatch<TreatmentReportFormData>;
  setValue: UseFormSetValue<TreatmentReportFormData>;
  patientId?: string;
  onActiveConsentChange?: (content: string | null) => void;
}

export const useTreatmentReportFrequency = ({
  control,
  watch,
  setValue,
  patientId,
  onActiveConsentChange,
}: UseTreatmentReportFrequencyParams) => {
  const { tenant, clinicNumber } = useClinicContext();
  const appModal = useAppModal();
  const svcType = useClinicPortalServiceType();
  const [isCalcLoading, setIsCalcLoading] = useState(false);
  const hasCalcOnceRef = useRef(false);

  const runCalcFrequency = useCallback(
    async (reportDate: string | undefined) => {
      if (!reportDate || !patientId || !tenant || !clinicNumber) return;
      const isoDate = reportDate.includes('年') ? japaneseDateToISO(reportDate) : reportDate;
      if (!isoDate) return;

      setIsCalcLoading(true);
      try {
        const secondFlag = !!watch('second_flag');
        const treatmentYearmonth = watch('treatment_yearmonth');
        const res = await searchTreatmentCount(
          tenant,
          clinicNumber,
          patientId,
          svcType,
          isoDate,
          secondFlag,
          treatmentYearmonth
        );
        // 施術の頻度 is required, so an uncomputable count falls back to 0 instead of
        // clearing the field and blocking save.
        setValue('treatment_monthly_avg', (res.data.count ?? 0) as any, { shouldDirty: true });
        onActiveConsentChange?.(res.data.active_consent_content);
      } catch {
        // Keep the field editable when recalculation fails.
      } finally {
        setIsCalcLoading(false);
      }
    },
    [patientId, tenant, clinicNumber, svcType, setValue, watch, onActiveConsentChange]
  );

  const watchedMonthlyAvg = useWatch({ control, name: 'treatment_monthly_avg' });
  useEffect(() => {
    if (watchedMonthlyAvg !== null && watchedMonthlyAvg !== undefined && watchedMonthlyAvg !== 0) {
      hasCalcOnceRef.current = true;
    }
  }, [watchedMonthlyAvg]);

  const handleCalcFrequency = useCallback(async () => {
    const reportDate = watch('treatment_report_date');
    if (!reportDate) {
      appModal.warning({ title: '作成年月日を入力してください' });
      return;
    }

    hasCalcOnceRef.current = true;
    await runCalcFrequency(reportDate);
  }, [watch, runCalcFrequency, appModal]);

  const watchedReportDate = useWatch({ control, name: 'treatment_report_date' });
  const prevReportDateRef = useRef<string | undefined>(undefined);
  const seenInitialDateRef = useRef(false);
  useEffect(() => {
    if (!seenInitialDateRef.current) {
      if (watchedReportDate) {
        seenInitialDateRef.current = true;
        prevReportDateRef.current = watchedReportDate;
      }
      return;
    }

    if (prevReportDateRef.current === watchedReportDate) return;
    prevReportDateRef.current = watchedReportDate;
    if (!hasCalcOnceRef.current) return;
    if (watchedReportDate) void runCalcFrequency(watchedReportDate);
  }, [watchedReportDate, runCalcFrequency]);

  const markFrequencyTouched = useCallback((value: unknown) => {
    if (value !== null && value !== undefined && value !== '') {
      hasCalcOnceRef.current = true;
    }
  }, []);

  return {
    handleCalcFrequency,
    isCalcLoading,
    markFrequencyTouched,
  };
};

